/**
 * Banner for curators when they are viewing their patient's
 * medical information and/or curating their records
 */
import { useLocation } from '@gatsbyjs/reach-router';
import { Box, Button, Stack, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import ContentLoader from '@components/ContentLoader';
import CurationSideDrawer from '@components/CurationSideDrawer';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import Icon from '@components/Icon/Icon';
import AddRecordModalContent from '@components/Modal/Content/AddRecordModalContent';

import {
  CLIENTS,
  DASHBOARD,
  DISCERN,
  DISCERN_JOBS,
  PATIENTS,
} from '@constants/routes';

import { useCuration } from '@hooks/useCuration';
import { useDrawer } from '@hooks/useDrawer';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useModal } from '@hooks/useModal';
import { useSession } from '@hooks/useSession';

import { formatName } from '@utils/format';

import { CurationStatus, RoleName } from 'generated/types';

import AssignedUserDropdown from '../AssignedUserDropdown/AssignedUserDropdown';
import { FullBanner } from './CurationBanner.style';
import CurationCard from './CurationCard';
import ProfileStatus from './PatientProfileStatus';

const messages = defineMessages({
  saveDraft: {
    id: 'curationBanner.saveDraft',
    defaultMessage: 'Save Draft',
  },
  needsReview: {
    id: 'curationBanner.needsReview',
    defaultMessage: 'Needs Review',
  },
  publish: {
    id: 'curationBanner.publish',
    defaultMessage: 'Publish',
  },
  publishConfirm: {
    id: 'curationBanner.publishConfirm',
    defaultMessage: 'Are you sure you want to publish this record?',
  },
  permanent: {
    id: 'curationBanner.permanent',
    defaultMessage: 'This cannot be undone.',
  },
  curateRecords: {
    id: 'curationBanner.curateRecords',
    defaultMessage: 'Add a Record',
  },
  beginQuestionnaire: {
    id: 'curationBanner.beginQuestionnaire',
    defaultMessage: 'Begin Questionnaire',
  },
  addRecord: {
    id: 'curationBanner.addRecord',
    defaultMessage: 'Add Record',
  },
  recordType: {
    id: 'curationBanner.recordType',
    defaultMessage: 'Record Type',
  },
  client: {
    id: 'curationBanner.client',
    defaultMessage: 'Client: Patient',
  },
  select: {
    id: 'GLOBAL.select',
    defaultMessage: 'Select',
  },
  clientOverview: {
    id: 'curationBanner.clientOverview',
    defaultMessage: 'Client Overview',
  },
  clientView: {
    id: 'clientProfile.clientView',
    defaultMessage: 'Client View',
  },
  discern: {
    id: 'curationBanner.discern',
    defaultMessage: 'Discern',
  },
  patientOverview: {
    id: 'curationBanner.patientOverview',
    defaultMessage: 'Patient Overview',
  },
});

const CurationBanner: React.FC = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const {
    openDrawer,
    setContent: setDrawerContent,
    state: { isOpen },
  } = useDrawer();
  const {
    state: { isCurating, vitall_patient_id, record, isEditing, vitall_patient },
    updateRecordCurationStatus,
    viewAsPatient,
  } = useCuration();
  const {
    state: { activeProfile, isVitall },
  } = useSession();
  const { flags } = useFeatureFlags();

  const loading = !vitall_patient;
  const assignedCuratorOrPractitioner = isVitall
    ? vitall_patient?.curator
    : vitall_patient?.practitioner;

  const { closeModal, openModal, setContent } = useModal();
  const user = vitall_patient?.user;
  const status = vitall_patient?.status;
  const profileView = location.pathname.includes('clients');

  const handleDraft = async () => {
    await updateRecordCurationStatus(CurationStatus.Draft);
  };

  const handleNeedsReview = async () => {
    await updateRecordCurationStatus(CurationStatus.NeedsReview);
  };

  const handlePublish = async () => {
    await updateRecordCurationStatus(CurationStatus.Published);
  };

  const handleDrawer = () => {
    setDrawerContent(
      <CurationSideDrawer
        name={activeProfile?.first_name}
        vitall_patient_id={activeProfile?.vitall_patient_id ?? null}
      />
    );
    openDrawer();
  };

  const handleOpenModal = (user_id?: string | null) => {
    if (!user_id) {
      return;
    }
    openModal({
      width: 400,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: (
        <AddRecordModalContent user_id={user_id} onClose={closeModal} />
      ),
    });
  };

  return (
    <>
      <FullBanner drawerOpen={isOpen}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            padding: 0.5,
            width: 'calc(100% - 82px)',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <ContentLoader loading={loading}>
              <Box>
                <Typography variant="h6">
                  {formatName(user?.first_name, user?.last_name)}
                </Typography>
                <Typography variant="subtitle1">
                  {formatMessage(messages.client)}
                </Typography>
              </Box>
              {isCurating ? (
                <CurationStatusBadge status={record?.curationStatus} />
              ) : (
                <>
                  {flags.isVitallPatientAssignUserEnabled && (
                    <AssignedUserDropdown
                      name={formatName(
                        assignedCuratorOrPractitioner?.first_name,
                        assignedCuratorOrPractitioner?.last_name
                      )}
                      vitall_patient_id={vitall_patient_id}
                      value={
                        isVitall
                          ? vitall_patient?.curator_user_id
                          : vitall_patient?.practitioner_user_id
                      }
                      role={isVitall ? RoleName.Curator : RoleName.Practitioner}
                    />
                  )}
                  {flags.isVitallPatientChangeStatusEnabled && (
                    <ProfileStatus
                      status={status ?? undefined}
                      vitall_patient_id={vitall_patient_id}
                    />
                  )}
                </>
              )}
            </ContentLoader>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {isCurating ? (
              <>
                {flags.isRecordChangeCurationStatusEnabled &&
                  record?.curationStatus !== CurationStatus.Published && (
                    <>
                      {record?.curationStatus !== CurationStatus.Draft && (
                        <Button
                          disabled={!(isEditing || isCurating)}
                          onClick={handleDraft}
                          color="secondary"
                        >
                          {formatMessage(messages.saveDraft)}
                        </Button>
                      )}
                      {record?.curationStatus !==
                        CurationStatus.NeedsReview && (
                        <Button
                          disabled={!(isEditing || isCurating)}
                          onClick={handleNeedsReview}
                          color="secondary"
                        >
                          {formatMessage(messages.needsReview)}
                        </Button>
                      )}
                      <ConfirmationDialog
                        content={formatMessage(messages.permanent)}
                        onConfirm={handlePublish}
                        title={formatMessage(messages.publishConfirm)}
                      >
                        {({ handleOpen }) => (
                          <Button
                            disabled={!(isEditing || isCurating)}
                            onClick={handleOpen}
                          >
                            {formatMessage(messages.publish)}
                          </Button>
                        )}
                      </ConfirmationDialog>
                    </>
                  )}

                {isEditing ? <CurationCard /> : null}
              </>
            ) : (
              <>
                {/* <Item>
                  <Button
                    disabled
                    
                    variant="white"
                    width="150px"
                  >
                    {formatMessage(messages.beginQuestionnaire)}
                  </Button>
                </Item> */}

                {isVitall && (
                  <Button
                    onClick={() => {
                      const vitallPatientId =
                        activeProfile?.vitall_patient?.vitall_patient_id;
                      if (vitallPatientId) {
                        navigate(DISCERN_JOBS);
                      } else {
                        navigate(DISCERN);
                      }
                    }}
                  >
                    {formatMessage(messages.discern)}
                  </Button>
                )}
                {profileView ? (
                  <Button
                    onClick={() => {
                      if (user?.user_id) {
                        viewAsPatient(user.user_id);
                        navigate(DASHBOARD);
                      }
                    }}
                  >
                    {formatMessage(messages.clientView)}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      if (activeProfile?.user_id) {
                        navigate(
                          `${isVitall ? CLIENTS : PATIENTS}${
                            activeProfile.user_id
                          }`
                        );
                      }
                    }}
                  >
                    {formatMessage(
                      isVitall
                        ? messages.clientOverview
                        : messages.patientOverview
                    )}
                  </Button>
                )}
                <Button onClick={() => handleOpenModal(user?.user_id)}>
                  {formatMessage(messages.addRecord)}
                </Button>
              </>
            )}

            {flags.isCuratorTaskListPatientTasksEnabled && (
              <Icon
                color="primary.main"
                name="list"
                onClick={handleDrawer}
                type="fas"
              />
            )}
          </Stack>
        </Stack>
      </FullBanner>
    </>
  );
};

export default CurationBanner;
